import React, { useEffect, useState, useContext } from 'react';
import { getAllVideos } from '../services/api';
import { AudioContext } from '../App';

const PageVideo = () => {
  // On récupère activeVideoId et setActiveVideoId depuis le contexte global
  const { pauseAudio, activeVideoId, setActiveVideoId } = useContext(AudioContext);
  const [videos, setVideos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Toutes");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await getAllVideos();
        setVideos(res.data);
      } catch (err) {
        console.error("Erreur chargement des vidéos", err);
      }
    };
    fetchVideos();
  }, []);

  const handlePlayVideo = (id) => {
    pauseAudio(); // Coupe la radio
    setActiveVideoId(id); // Définit l'ID global (arrête le flux précédent ou le direct)
  };

  const categories = ["Toutes", ...new Set(videos.map(v => v.category).filter(Boolean))];

  const filteredVideos = videos
    .filter(v => {
      const matchesSearch = v.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === "Toutes" || v.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = (match && match[7].length === 11) ? match[7] : null;
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0` : url;
  };

  const getThumb = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) 
      ? `https://img.youtube.com/vi/${match[7]}/hqdefault.jpg` 
      : 'https://via.placeholder.com/480x270?text=Fréquence+974';
  };

  return (
    <div className="bg-light min-vh-100">
      <div className="bg-white border-bottom py-5 mb-5 shadow-sm">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="fw-black text-uppercase display-5 mb-1">Vidéothèque</h1>
              <p className="text-muted fw-bold">Retrouvez tous nos replays et émissions en exclusivité.</p>
            </div>
            <div className="col-md-6">
              <div className="row g-2">
                <div className="col-12">
                  <div className="input-group shadow-sm">
                    <span className="input-group-text bg-white border-end-0"><i className="bi bi-search"></i></span>
                    <input 
                      type="text" 
                      className="form-control border-start-0" 
                      placeholder="Rechercher un titre..." 
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-6">
                  <select className="form-select shadow-sm" onChange={(e) => setSelectedCategory(e.target.value)}>
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <select className="form-select shadow-sm" onChange={(e) => setSortOrder(e.target.value)}>
                    <option value="desc">Plus récents</option>
                    <option value="asc">Plus anciens</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="container pb-5">
        <div className="row justify-content-center">
          <div className="col-12">
            <div className="row g-4">
              {filteredVideos.length > 0 ? filteredVideos.map(v => (
                <div key={v.id} className="col-sm-6 col-lg-4 col-xl-3">
                  <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden video-card bg-white">
                    <div className="position-relative aspect-video bg-dark">
                      {/* On utilise activeVideoId au lieu de playingId */}
                      {activeVideoId === v.id ? (
                        <iframe
                          src={getYouTubeEmbedUrl(v.url_video)}
                          title={v.title}
                          className="w-100 h-100 border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      ) : (
                        <div 
                          className="w-100 h-100 video-thumbnail-container" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handlePlayVideo(v.id)}
                        >
                          <img src={getThumb(v.url_video)} className="w-100 h-100 object-fit-cover" alt={v.title} />
                          <div className="position-absolute top-50 start-50 translate-middle play-icon-overlay">
                             <i className="bi bi-play-fill text-white display-1 opacity-75"></i>
                          </div>
                          <span className="position-absolute top-0 start-0 m-3 badge bg-danger text-uppercase">{v.category || "Replay"}</span>
                        </div>
                      )}
                    </div>
                    <div className="card-body p-3">
                      <h6 className="fw-black text-dark mb-1 text-uppercase text-truncate" style={{ fontSize: '0.85rem' }}>{v.title}</h6>
                      <small className="text-muted fw-bold" style={{ fontSize: '10px' }}>
                        <i className="bi bi-calendar3 me-1"></i>
                        {new Date(v.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </small>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="col-12 text-center py-5">
                  <h3 className="text-muted opacity-50 fw-black text-uppercase">Aucune vidéo trouvée</h3>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .fw-black { font-weight: 900; }
        .aspect-video { aspect-ratio: 16 / 9; }
        .play-icon-overlay {
            transition: all 0.3s ease;
            transform: translate(-50%, -50%) scale(0.8);
            opacity: 0;
        }
        .video-thumbnail-container:hover .play-icon-overlay {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
        }
        .video-card { transition: transform 0.2s ease; }
        .video-card:hover { transform: translateY(-5px); }
        .form-select, .form-control { border-radius: 10px; font-weight: 600; font-size: 0.9rem; }
      `}</style>
    </div>
  );
};

export default PageVideo;